import { parseInline } from './inline.ts'
import type { Block, ListItem } from './types.ts'

const FENCE = /^\s*```+\s*(\S+)?\s*$/
const HEADING = /^(#{1,6})\s+(.*)$/
const RULE = /^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/
const QUOTE = /^\s*>\s?(.*)$/
const BULLET = /^(\s*)(?:[-*+]|\d+[.)])\s+(.*)$/
const TASK = /^\[([ xX])\]\s+(.*)$/
const ORDERED = /^\s*\d+[.)]\s/

/** Two spaces per level; a four-space indent simply lands two levels deep. */
const INDENT = 2

/**
 * A line-driven reader for the subset an issue body actually uses — deliberately
 * not CommonMark. Unsupported constructs fall through to their source text: a
 * table renders as the pipes the author typed, where dropping it would lose
 * content the surface exists to show.
 */
export function parseMarkdown(src: string): Block[] {
  return parseBlocks(src, 0)
}

/**
 * Deeper than any real quote chain, and the recursion has to stop somewhere: the
 * prose is external, one level is spent per leading `>`, and an adversarial
 * chain would take the stack — and the whole panel — down with it.
 */
const MAX_QUOTE_DEPTH = 8

function parseBlocks(src: string, quoteDepth: number): Block[] {
  const lines = src.replace(/\r\n?/g, '\n').split('\n')
  const blocks: Block[] = []

  let paragraph: string[] = []
  const flush = (): void => {
    if (paragraph.length === 0) return
    blocks.push({ kind: 'paragraph', content: parseInline(paragraph.join('\n')) })
    paragraph = []
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!

    const fence = FENCE.exec(line)
    if (fence) {
      flush()
      const body: string[] = []
      // An unterminated fence takes the rest of the document, which is what the
      // author sees on GitHub too — closing it for them would invent structure.
      for (i++; i < lines.length && !FENCE.test(lines[i]!); i++) body.push(lines[i]!)
      blocks.push({ kind: 'code', lang: fence[1] ?? null, value: body.join('\n') })
      continue
    }

    if (line.trim() === '') {
      flush()
      continue
    }

    if (RULE.test(line)) {
      flush()
      blocks.push({ kind: 'rule' })
      continue
    }

    const heading = HEADING.exec(line)
    if (heading) {
      flush()
      blocks.push({
        kind: 'heading',
        level: heading[1]!.length,
        content: parseInline(heading[2]!),
      })
      continue
    }

    if (QUOTE.test(line)) {
      flush()
      const quoted: string[] = []
      for (; i < lines.length; i++) {
        const q = QUOTE.exec(lines[i]!)
        if (!q) break
        quoted.push(q[1]!)
      }
      i--
      const inner = quoted.join('\n')
      blocks.push(
        quoteDepth < MAX_QUOTE_DEPTH
          ? { kind: 'quote', blocks: parseBlocks(inner, quoteDepth + 1) }
          : // Past the cap the remaining `>` stay in the text, which is legible.
            { kind: 'paragraph', content: parseInline(inner) },
      )
      continue
    }

    if (BULLET.test(line)) {
      flush()
      const ordered = ORDERED.test(line)
      const items: ListItem[] = []
      for (; i < lines.length; i++) {
        const bullet = BULLET.exec(lines[i]!)
        if (!bullet) break
        // A marker change ends the list: swallowing `- two` after `1. one` would
        // number a bullet, which is not what either line says.
        if (ORDERED.test(lines[i]!) !== ordered) break
        const task = TASK.exec(bullet[2]!)
        items.push({
          checked: task ? task[1]!.toLowerCase() === 'x' : null,
          depth: Math.floor(bullet[1]!.replace(/\t/g, '  ').length / INDENT),
          content: parseInline(task ? task[2]! : bullet[2]!),
        })
      }
      i--
      blocks.push({ kind: 'list', ordered, items })
      continue
    }

    paragraph.push(line)
  }

  flush()
  return blocks
}
